import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Download, Mail, Merge, Phone, Plus, Search } from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { clients as clientsApi } from '@/api/endpoints'
import { EmptyState, Input, Modal, Spinner } from '@/components/ui'
import ColumnPicker, { useVisibleColumns } from '@/components/ui/ColumnPicker'
import Pagination from '@/components/ui/Pagination'
import { SortableHeader, useOrdering } from '@/components/ui/SortableHeader'
import ClientDetailModal from '@/features/clients/ClientDetailModal'
import NewClientModal from '@/features/clients/NewClientModal'
import { useCodebooks } from '@/hooks/useBootstrap'
import { cn, formatDate, formatMoney } from '@/lib/utils'
import { useAuth } from '@/store/auth'
import type { Client } from '@/types/api'

export default function ClientsPage() {
  const { t } = useTranslation()
  const readOnly = !useAuth((s) => s.user)?.can_write

  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [mergeOpen, setMergeOpen] = useState(false)
  const [detailId, setDetailId] = useState<number | null>(null)
  const [creating, setCreating] = useState(false)
  const [downloading, setDownloading] = useState<'xlsx' | 'csv' | null>(null)

  const columns = useMemo(
    () => COLUMNS.map((column) => ({ ...column, label: t(column.labelKey) })),
    [t],
  )
  const { toggle: toggleColumn, isVisible, reset } = useVisibleColumns(
    'l2s:clientColumns',
    columns,
    DEFAULT_COLUMNS,
  )
  const { ordering, toggle } = useOrdering('sname', 'l2s:clientOrdering')

  const { data, isLoading } = useQuery({
    queryKey: ['clients', query, page, pageSize, ordering],
    queryFn: () =>
      clientsApi.list({ q: query || undefined, page, limit: pageSize, ordering }),
    placeholderData: keepPreviousData,
  })

  const totalPages = Math.max(1, Math.ceil((data?.count ?? 0) / pageSize))

  async function download(filetype: 'xlsx' | 'csv') {
    setDownloading(filetype)
    try {
      // Rovnaký filter ako na obrazovke - celý zoznam, nie len jedna strana.
      const { blob, filename } = await clientsApi.exportUrl(filetype, query || undefined)
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      link.click()
      URL.revokeObjectURL(url)
    } finally {
      setDownloading(null)
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-lg font-semibold">{t('clients.title')}</h1>
        <span className="text-sm text-slate-400">{data?.count ?? 0}</span>

        <div className="relative ml-auto">
          <Search size={15} className="absolute left-2.5 top-2.5 text-slate-400" />
          <Input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setPage(1)
            }}
            placeholder={t('clients.searchPlaceholder')}
            className="w-72 pl-8"
          />
        </div>

        <ColumnPicker
          columns={columns}
          isVisible={isVisible}
          onToggle={toggleColumn}
          onReset={reset}
        />

        <button
          type="button"
          className="btn-secondary"
          disabled={downloading !== null}
          onClick={() => download('xlsx')}
        >
          {downloading === 'xlsx' ? <Spinner /> : <Download size={15} />}
          {t('clients.exportXlsx')}
        </button>
        <button
          type="button"
          className="btn-secondary"
          disabled={downloading !== null}
          onClick={() => download('csv')}
        >
          {downloading === 'csv' ? <Spinner /> : <Download size={15} />}
          {t('clients.exportCsv')}
        </button>

        {!readOnly ? (
          <>
            <button type="button" className="btn-secondary" onClick={() => setMergeOpen(true)}>
              <Merge size={15} />
              {t('clients.merge')}
            </button>
            <button type="button" className="btn-primary" onClick={() => setCreating(true)}>
              <Plus size={15} />
              {t('clients.create')}
            </button>
          </>
        ) : null}
      </div>

      <div className="card overflow-x-auto">
        {isLoading ? (
          <div className="flex h-40 items-center justify-center text-slate-400">
            <Spinner className="h-5 w-5" />
          </div>
        ) : (data?.results.length ?? 0) === 0 ? (
          <EmptyState>{t('app.empty')}</EmptyState>
        ) : (
          <>
            {/* Trinásť stĺpcov sa na notebook nezmestí, preto sa tabuľka
                posúva vodorovne namiesto toho, aby sa text lámal. */}
            <div className="overflow-x-auto">
              <table className="table-base">
                <thead>
                  <tr>
                    {columns
                      .filter((column) => isVisible(column.key))
                      .map((column) => (
                        <SortableHeader
                          key={column.key}
                          field={'sort' in column ? column.sort : undefined}
                          label={column.label}
                          ordering={ordering}
                          onSort={(field) => {
                            toggle(field)
                            setPage(1)
                          }}
                          align={NUMERIC.has(column.key) ? 'right' : 'left'}
                        />
                      ))}
                  </tr>
                </thead>
                <tbody>
                  {data?.results.map((client) => (
                    <ClientRow
                      key={client.id}
                      client={client}
                      isVisible={isVisible}
                      onOpen={() => setDetailId(client.id)}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              page={page}
              pages={totalPages}
              pageSize={pageSize}
              count={data?.count ?? 0}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size)
                setPage(1)
              }}
            />
          </>
        )}
      </div>

      {mergeOpen ? <MergeModal onClose={() => setMergeOpen(false)} /> : null}
      {creating ? (
        <NewClientModal
          onClose={() => setCreating(false)}
          // Po založení sa otvorí detail - takmer vždy nasleduje kontrola
          // údajov alebo rovno rezervácia.
          onCreated={(client) => setDetailId(client.id)}
        />
      ) : null}

      {detailId ? (
        <ClientDetailModal clientId={detailId} onClose={() => setDetailId(null)} />
      ) : null}
    </div>
  )
}

/** Poradie stĺpcov v tabuľke. Kľúč sa ukladá do `localStorage`, takže sa
 *  premenovať nedá bez toho, aby sa ľuďom stratila ich voľba. */
// `sort` je názov poľa v API (`ordering_fields` v ClientViewSet). Kde chýba,
// sa stĺpec zoraďovať nedá - jazyk a kurz sú id do číselníka, takže by sa
// zoradili podľa čísla, nie podľa názvu, čo je horšie než nič.
const COLUMNS = [
  { key: 'name', labelKey: 'reservation.client', sort: 'sname', fixed: true },
  { key: 'tel', labelKey: 'reservation.phone', sort: 'tel' },
  { key: 'email', labelKey: 'reservation.email', sort: 'email' },
  { key: 'level', labelKey: 'clients.level', sort: 'levelid' },
  { key: 'language', labelKey: 'reservation.language' },
  { key: 'course', labelKey: 'reservation.course' },
  { key: 'children', labelKey: 'global.child', sort: 'is_children' },
  { key: 'beginner', labelKey: 'global.beginner', sort: 'is_beginner' },
  { key: 'note', labelKey: 'global.note' },
  { key: 'count', labelKey: 'clients.reservationsCount', sort: 'reservations_count' },
  { key: 'last', labelKey: 'clients.lastReservation', sort: 'last_reservation' },
  { key: 'spent', labelKey: 'clients.totalSpent', sort: 'total_spent' },
  { key: 'created', labelKey: 'clients.created', sort: 'created' },
] as const

type ColumnKey = (typeof COLUMNS)[number]['key']

/** Čo je vidieť, kým si to niekto neprepne. Zvyšok je za výberom stĺpcov. */
const DEFAULT_COLUMNS: ColumnKey[] = [
  'name', 'tel', 'email', 'level', 'count', 'last', 'spent',
]

const NUMERIC: ReadonlySet<ColumnKey> = new Set(['count', 'spent'])

/**
 * Jeden riadok zoznamu, jeden údaj v jednej bunke.
 *
 * Stĺpcov je trinásť, takže sa tabuľka na užšej obrazovke posúva vodorovne.
 * Alternatíva - stláčať údaje po dvoch do bunky - robí riadky rôzne vysoké
 * a zle sa v nich hľadá očami.
 */
function ClientRow({
  client,
  isVisible,
  onOpen,
}: {
  client: Client
  isVisible: (key: ColumnKey) => boolean
  onOpen: () => void
}) {
  const { t } = useTranslation()
  const books = useCodebooks()

  const cells: Record<ColumnKey, ReactNode> = {
    name: (
      <span className="font-medium text-brand-600">{client.full_name || `#${client.id}`}</span>
    ),
    tel: client.tel || '—',
    email: <span className="text-slate-500">{client.email || '—'}</span>,
    level: books.label('levels', client.levelid) || '—',
    language: books.label('languages', client.lang_id) || '—',
    course: books.label('courses', client.course_id) || '—',
    children: client.is_children ? <Check size={14} className="text-emerald-600" /> : '—',
    beginner: client.is_beginner ? <Check size={14} className="text-emerald-600" /> : '—',
    note: client.note ? (
      // Jeden riadok - poznámka býva dlhá a inak by riadok narástol do výšky.
      <span className="block max-w-[18rem] truncate text-slate-500" title={client.note}>
        {client.note}
      </span>
    ) : (
      '—'
    ),
    count: client.reservations_count ?? 0,
    last: formatDate(client.last_reservation ?? null) || '—',
    spent: formatMoney(client.total_spent),
    created: <span className="text-slate-400">{formatDate(client.created) || '—'}</span>,
  }

  return (
    <tr
      className="cursor-pointer hover:bg-slate-50"
      onClick={onOpen}
      // Riadok musí byť dosiahnuteľný aj z klávesnice, inak je detail bez myši
      // nedostupný.
      tabIndex={0}
      role="button"
      aria-label={t('clients.openDetail')}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onOpen()
        }
      }}
    >
      {COLUMNS.filter((column) => isVisible(column.key)).map((column) => (
        <td
          key={column.key}
          className={cn(
            'whitespace-nowrap',
            NUMERIC.has(column.key) ? 'text-right tabular-nums' : undefined,
          )}
        >
          {cells[column.key]}
        </td>
      ))}
    </tr>
  )
}

function MergeModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [done, setDone] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['client-duplicates'],
    queryFn: clientsApi.duplicates,
  })

  const merge = useMutation({
    mutationFn: ({ targetId, sourceIds }: { targetId: number; sourceIds: number[] }) =>
      clientsApi.merge(targetId, sourceIds),
    onSuccess: async (result) => {
      // Zoznam duplicít sa musí obnoviť, inak by v ňom zostali práve zmazaní
      // klienti a ďalšie zlúčenie by skončilo chybou.
      await qc.invalidateQueries({ queryKey: ['client-duplicates'] })
      void qc.invalidateQueries({ queryKey: ['clients'] })
      setDone(
        t('clients.mergeDone', {
          count: result.moved.clients_deleted ?? 0,
          name: result.client.full_name,
        }),
      )
    },
  })

  return (
    <Modal open onClose={onClose} size="lg" title={t('clients.merge')}>
      {done ? (
        <div className="mb-3 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          {done}
        </div>
      ) : null}

      {merge.isError ? (
        <div className="mb-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          {errorMessage(merge.error) ?? t('clients.mergeFailed')}
        </div>
      ) : null}

      {isLoading ? (
        <div className="flex h-32 items-center justify-center text-slate-400">
          <Spinner className="h-5 w-5" />
        </div>
      ) : (data?.length ?? 0) === 0 ? (
        <EmptyState>{t('clients.noDuplicates')}</EmptyState>
      ) : (
        <div className="space-y-4">
          {data?.map((group) => (
            <DuplicateGroup
              // Kľúč z id členov: po zlúčení sa skupina zmení a stav výberu
              // sa má postaviť nanovo, nie sa preniesť na iných klientov.
              key={group.clients.map((c) => c.id).join('-')}
              reasons={group.reasons}
              clients={group.clients}
              busy={merge.isPending}
              onMerge={(targetId, sourceIds) => {
                setDone(null)
                merge.mutate({ targetId, sourceIds })
              }}
            />
          ))}
        </div>
      )}
    </Modal>
  )
}

function errorMessage(error: unknown): string | null {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data
    ?.detail
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail) && typeof detail[0] === 'string') return detail[0]
  return null
}

/** `tel:905123456` → `905123456`, s ikonkou podľa druhu kontaktu. */
function ReasonChip({ reason }: { reason: string }) {
  const [kind, value] = reason.includes(':') ? reason.split(/:(.*)/s) : ['', reason]
  const Icon = kind === 'email' ? Mail : Phone
  return (
    <span className="inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">
      <Icon size={11} />
      {value}
    </span>
  )
}

function DuplicateGroup({
  reasons,
  clients,
  busy,
  onMerge,
}: {
  reasons: string[]
  clients: Client[]
  busy: boolean
  onMerge: (targetId: number, sourceIds: number[]) => void
}) {
  const { t } = useTranslation()

  // Prežívajúci: predvolene ten s najviac lekciami - po ňom zostane najviac
  // histórie na mieste, takže je najmenej čo presúvať.
  const [target, setTarget] = useState<number>(
    () =>
      [...clients].sort(
        (a, b) => (b.reservations_count ?? 0) - (a.reservations_count ?? 0) || a.id - b.id,
      )[0]?.id ?? 0,
  )
  const [selected, setSelected] = useState<number[]>(() => clients.map((c) => c.id))
  const [confirming, setConfirming] = useState(false)

  // Zlučuje sa len to, čo je zaškrtnuté - v skupine môže byť aj niekto, kto
  // len zdieľa telefón s rodinou, a toho zlúčiť nechceme.
  const sources = selected.filter((id) => id !== target)
  const survivor = clients.find((c) => c.id === target)

  const toggle = (id: number) =>
    setSelected((current) =>
      current.includes(id) ? current.filter((x) => x !== id) : [...current, id],
    )

  return (
    <div className="rounded-md border border-slate-200 p-3">
      <div className="mb-2 flex flex-wrap items-center gap-1">
        {reasons.map((reason) => (
          <ReasonChip key={reason} reason={reason} />
        ))}
        <span className="ml-auto text-xs text-slate-400">
          {t('clients.mergeTargetHint')}
        </span>
      </div>

      <table className="w-full text-sm">
        <tbody>
          {clients.map((client) => {
            const isTarget = client.id === target
            return (
              <tr key={client.id} className={isTarget ? 'bg-emerald-50/60' : undefined}>
                <td className="w-8 py-1 pl-1">
                  <input
                    type="checkbox"
                    aria-label={t('clients.mergeInclude')}
                    checked={isTarget || selected.includes(client.id)}
                    disabled={isTarget}
                    onChange={() => toggle(client.id)}
                    className="text-brand-500 disabled:opacity-40"
                  />
                </td>
                <td className="w-8 py-1">
                  <input
                    type="radio"
                    aria-label={t('clients.mergeTarget')}
                    name={`target-${clients.map((c) => c.id).join('-')}`}
                    checked={isTarget}
                    onChange={() => {
                      setTarget(client.id)
                      setConfirming(false)
                    }}
                    className="text-emerald-600"
                  />
                </td>
                <td className="py-1">
                  <Link
                    to={`/clients/${client.id}`}
                    className="font-medium text-brand-600 hover:underline"
                  >
                    {client.full_name || `#${client.id}`}
                  </Link>
                  {isTarget ? (
                    <span className="ml-1 text-xs font-medium text-emerald-700">
                      {t('clients.mergeKeeps')}
                    </span>
                  ) : null}
                </td>
                <td className="py-1 text-xs text-slate-500">{client.tel || '—'}</td>
                <td className="py-1 text-xs text-slate-500">{client.email || '—'}</td>
                <td className="py-1 text-right text-xs tabular-nums text-slate-500">
                  {client.reservations_count ?? 0} ×
                </td>
                <td className="py-1 text-right text-xs tabular-nums text-slate-500">
                  {formatMoney(client.total_spent)}
                </td>
                <td className="py-1 pr-1 text-right text-xs text-slate-400">
                  {formatDate(client.last_reservation ?? null) || '—'}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>

      {confirming ? (
        <div className="mt-2 flex flex-wrap items-center gap-2 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <span className="flex-1">
            {t('clients.mergeConfirmDetail', {
              count: sources.length,
              name: survivor?.full_name ?? `#${target}`,
            })}
          </span>
          <button
            type="button"
            className="btn-primary"
            disabled={busy}
            onClick={() => {
              setConfirming(false)
              onMerge(target, sources)
            }}
          >
            {busy ? <Spinner /> : null}
            {t('global.confirm')}
          </button>
          <button type="button" className="btn-secondary" onClick={() => setConfirming(false)}>
            {t('global.cancel')}
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="btn-primary mt-2"
          disabled={busy || sources.length === 0}
          onClick={() => setConfirming(true)}
        >
          <Merge size={15} />
          {t('clients.mergeCount', { count: sources.length })}
        </button>
      )}
    </div>
  )
}
